import React from 'react';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '@app/ErrorBoundary';
import CrashedComponent from '@app/components/errors/CrashedComponent';
import Button from '@app/ui/sports-components/Button';
import ReFilters from '@app/components/filters';
import { FILTER_ORIGIN } from '@app/constants/filters';
import {
    Flex,
    Typography,
    Switch,
    Space,
    Button as AntdButton,
    Spin,
    Row,
    Col,
    SearchSelect,
} from '@app/ui';
import {
    getSelectedValues,
    toSearchSelectOptions,
    toSelectedObject,
} from '@app/utils/multiSelectValue';
import BroadcastTabs from './components/BroadcastTabs';
import ConnectedBroadcastsProvider, {
    BroadcastsContext,
} from './BroadcastsContext';
import FiltersOverrideRow from './components/FiltersOverrideRow';
import { Outlet } from 'react-router-dom';
import { BroadcastTab } from './utils/broadcastsUtils';

const Broadcasts = () => {
    const { t } = useTranslation('measurement');

    return (
        <ConnectedBroadcastsProvider>
            <BroadcastsContext.Consumer>
                {({
                    activeTab,
                    selectedNames,
                    viewershipType,
                    isDownloadingFrames,
                    shouldDisplayPageFilters,
                    nameOptions,
                    hasPostsAvailable,
                    _fetchFirstPage,
                    downloadFrames,
                    title,
                    toggleViewershipType,
                    isLoading,
                    setActiveTab,
                    selectNames,
                    filtersOverride,
                }) => (
                    <Flex
                        vertical
                        style={{
                            padding: 24,
                        }}
                        justify="space-between"
                    >
                        <Flex vertical>
                            <Typography.Title
                                level={4}
                                style={{
                                    color: '#1f1f1f',
                                    fontWeight: 400,
                                    paddingLeft: 12,
                                    fontSize: 32,
                                }}
                            >
                                {title}
                            </Typography.Title>
                            <FiltersOverrideRow />
                        </Flex>

                        <div>
                            <Flex
                                style={{
                                    marginTop: 12,
                                }}
                            >
                                <BroadcastTabs
                                    activeTab={activeTab}
                                    onTabSelection={setActiveTab}
                                />
                                <Flex
                                    style={{
                                        marginBottom: 16,
                                        borderBottom: '1px solid #BFBFBF',
                                        width: '100%',
                                    }}
                                    justify="flex-end"
                                >
                                    {activeTab === BroadcastTab.VIEWERSHIP && (
                                        <Flex
                                            justify="flex-end"
                                            style={{
                                                marginTop: 12,
                                            }}
                                        >
                                            <Space align="start">
                                                <Typography.Text>
                                                    {t(
                                                        'broadcastExposure.hhViewership',
                                                    )}{' '}
                                                </Typography.Text>
                                                <Switch
                                                    checked={
                                                        viewershipType ===
                                                        'individual'
                                                    }
                                                    onChange={
                                                        toggleViewershipType
                                                    }
                                                    checkedChildren=" "
                                                    unCheckedChildren=" "
                                                />
                                                <Typography.Text>
                                                    {t(
                                                        'broadcastExposure.indViewership',
                                                    )}
                                                </Typography.Text>
                                            </Space>
                                        </Flex>
                                    )}
                                    {activeTab === BroadcastTab.EXPOSURES && (
                                        <Flex justify="flex-end" align="center">
                                            <AntdButton
                                                onClick={downloadFrames}
                                                data-test="selected-metrics"
                                                color="primary"
                                                variant="outlined"
                                                style={{
                                                    padding: 8,
                                                    minWidth: 150,
                                                }}
                                                disabled={
                                                    isLoading ||
                                                    isDownloadingFrames ||
                                                    !hasPostsAvailable
                                                }
                                            >
                                                {isDownloadingFrames ? (
                                                    <>
                                                        <Spin size="small" />
                                                        <br />
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="fa-regular fa-download -m-r-1" />
                                                        {t(
                                                            'broadcastExposure.downloadFrames',
                                                        )}
                                                    </>
                                                )}
                                            </AntdButton>
                                        </Flex>
                                    )}
                                </Flex>
                            </Flex>

                            {!shouldDisplayPageFilters && (
                                <Row gutter={16} className="-m-b-2">
                                    <Col md={16}>
                                        <SearchSelect
                                            mode="multiple"
                                            placeholder={t(
                                                'broadcastExposure.broadcastNames',
                                            )}
                                            size="sm"
                                            options={toSearchSelectOptions(
                                                nameOptions,
                                            )}
                                            value={getSelectedValues(
                                                nameOptions,
                                                selectedNames,
                                            )}
                                            onChange={value =>
                                                selectNames(
                                                    toSelectedObject(value),
                                                )
                                            }
                                            disabled={isLoading}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Button
                                            className="gds-button--block -m-b-0"
                                            context="primary"
                                            onClick={_fetchFirstPage}
                                            size="sm"
                                            disabled={isLoading}
                                        >
                                            {t('broadcastExposure.update')}
                                        </Button>
                                    </Col>
                                </Row>
                            )}

                            {shouldDisplayPageFilters && (
                                <ErrorBoundary
                                    FallbackComponent={CrashedComponent}
                                >
                                    <ReFilters
                                        origin={
                                            FILTER_ORIGIN.BROADCAST_EXPOSURES
                                        }
                                        isOverridingFilters={!!filtersOverride}
                                    />
                                </ErrorBoundary>
                            )}

                            <Outlet />
                        </div>
                    </Flex>
                )}
            </BroadcastsContext.Consumer>
        </ConnectedBroadcastsProvider>
    );
};

export default Broadcasts;
